<template>
  <div class="zedplan-player player" data-vjs-player>
    <video
      class="video-js vjs-default-skin vjs-big-play-centered"
      width="100%"
      ref="player"
      muted="muted"
    ></video>
  </div>
</template>

<script>
import videojs from "video.js";
import "video.js/dist/video-js.css";
import "@/modules/videojs-quality-selector/plugin.css";
import("@/modules/videojs-quality-selector/plugin");

import { useSwiper } from "swiper/vue";

import UserVideoConfig from "@/modules/video/UserConfig";

import inViewport from "vue-in-viewport-mixin";
import ClickAndHold from "~/modules/ClickAndHold/ClickAndHold";


export default {
  mixins: [inViewport],
  props: {
    data: {
      type: Object,
      required: true,
    },
    poster: {
      type: String,
      required: false,
    },
    title: {
      type: String,
      required: false,
    },
    subtitle: {
      type: String,
      required: false,
    },
    index: {
      type: Number,
      required: true,
    },
  },
  computed: {
    source() {
      return this.data.playlist_url;
    },
  },
  data() {
    let swiper = useSwiper();
    if (!swiper) {
      swiper = {
        activeIndex: 0,
      };
    }

    return {
      player: null,
      playing: false,
      asking: false,
      holdingClick: false,
      swiper,
      isVisible: false,
    };
  },
  methods: {
    isCurrent() {
      return this.swiper.activeIndex === this.index;
    },
    initPlayer() {
      this.player = videojs(this.$refs.player, {
        autoplay: false,
        loop: true,
        controls: UserVideoConfig.isIOS,
        languages: {
          es: import("@/assets/lang/videojs/es.json"),
          en: import("@/assets/lang/videojs/en.json"),
        },
        language: this.$i18n.locale,
        techOrder: ["html5"],
        poster: this.poster,
        controlBar: {
          children: [
            "playToggle",
            "progressControl",
            "volumePanel",
            "fullscreenToggle"
          ],
        },
        notSupportedMessage: this.$t("player.not_supported"),
        html5: {
          hls: {
            overrideNative: true,
            limitRenditionByPlayerDimensions: !useRuntimeConfig().public.VUE_APP_AUTO_QUALITY_VIDEO,
            bandwidth: 6194304
          },
        },
        sources: [
          {
            src: this.source,
            type: "application/x-mpegURL",
            // src: "/video/test.mp4",
            // type: "video/mp4",
          },
        ],
      });

      this.player.QualitySelector({
        // maximumDefaultQuality: {
        //   bitrate: 1536000,
        // },
      });

      this.player.enableTouchActivity();
      this.player.player_.handleTechClick_ = function () {};
    },
    pausePlayer() {
      if (!this.player) return;
      this.player.pause();
      this.playing = false;
      this.asking = false;
    },
    playPlayer() {
      if (!this.player || this.playing || !this.isCurrent()) return;

      try {
        this.player.pause();
        setTimeout(() => { this.player.play();}, 30);

        this.playing = true;
        this.asking = false;
      } catch (error) {
        console.error(error);
      }
    },
    hardPlay() {
      this.playPlayer();
      this.$emit("videoPriority", this._handler);
    },
    askForPlay() {
      if (this.asking || !this.isCurrent()) return;
      this.asking = true;
      this.$emit("videoAppend", this._handler);
    },
    unsetVideo() {
      this.pausePlayer();
      this.$emit("videoRemove");
    },
    _handler(option, value) {
      if (!this.player || !this.isCurrent()) return;

      if (option === "play") this.playPlayer();
      else if (option === "pause") this.pausePlayer();
      else if (option === "mute") this.player.muted(value);
    },
  },
  mounted() {
    this.$refs.player.addEventListener(
      "ended",
      () => {
        this.$emit("videoEnded");
      },
      false
    );

    this.$refs.player.addEventListener(
      "play",
      () => {
        this.$emit("videoStarted");
      },
      false
    );

    const ClickHandler = () => {
      if (this.playing) return this.unsetVideo();
      this.hardPlay();
    };
    const TouchHandler = () => {
      if (this.holdingClick) return;
      this.$emit("videoMute");
    };

    if (UserVideoConfig.isMobile) {
      const onActiveHolding = () => {
        this.holdingClick = true;
        this.pausePlayer();
      };

      const onReleaseHolding = () => {
        this.playPlayer();
        setTimeout(() => {
          this.holdingClick = false;
        }, 100);
      };

      new ClickAndHold(
        this.$refs.player,
        onActiveHolding,
        onReleaseHolding,
        500
      );
      this.$refs.player.addEventListener("touchend", TouchHandler, false);
    } else {
      this.$refs.player.addEventListener("click", ClickHandler, false);
    }

    this.initPlayer();

    if (typeof this.swiper.on === "function") {
      this.swiper.on("slideChange", () => {
        if (this.isVisible && this.isCurrent()) this.askForPlay();
        else if (this.playing) this.pausePlayer();
        else this.unsetVideo();
      });
    }
  },
  beforeDestroy() {
    if (this.player) {
      this.player.dispose();
    }
    this.player = null;
  },
  watch: {
    "inViewport.now": function (visible) {
      this.isVisible = visible;
      if (visible) this.playPlayer();
      else this.pausePlayer();
    },
  },
};
</script>

<style scoped lang="scss">
.player {
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  width: 100%;
}

.video-js {
  width: 100%;
  background-color: unset;
  height: 100%;

  @media (max-width: 900px) {
    min-height: 300px;
    height: auto;
  }

  @media (max-width: 480px) {
    min-height: 100px;
    height: -webkit-fill-available;
    height: -moz-available;
  }

  .vjs-tech {
    // height: unset;
    height: -webkit-fill-available;
    height: -moz-available;
    position: relative;
    max-height: 90vh;
  }
}
</style>

<style lang="scss">
.video-js {
  .vjs-poster {
    background-size: cover;
  }

  // @media(max-width: 480px){
  //     .vjs-poster{
  //         background-size: cover;
  //     }
  // }

  .vjs-error-display.vjs-modal-dialog {
    background: unset;

    .vjs-modal-dialog-content {
      color: #000a;
    }
  }
}
</style>
